import { Formik } from 'formik';
import { useContext } from 'react';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import useSWR from 'swr';
import { OrganizationMember, OrganizationMemberInvite, OrganizationRole } from '../../../../../../../../typings/OrganizationMember.interface';
import { APISubscription } from '../../../../../../../../typings/Subscription.interface';
import Analytics from '../../../../../client/Analytics';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import { isFreePlan } from '../../../../../utils/subscription';
import Alert from '../../../../common/base/Alert';
import Button, { PlaceholderButton } from '../../../../common/base/Button';
import SelectInput from '../../../../common/Form/Fields/SelectInput';
import TextInput from '../../../../common/Form/Fields/TextInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import Modal from '../../../../common/Modal';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { DashboardContext } from '../../DashboardContext';
import { createRoleOptions } from './OrganizationMembers';
interface Props {
  onMembersAdded: (members: OrganizationMember[], invites: OrganizationMemberInvite[], subscription: APISubscription) => void;
}
const AddOrganizationMemberModal: React.FC<Props> = ({
  onMembersAdded
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    organization,
    subscription
  } = useContext(DashboardContext);
  const history = useHistory();
  const location = useLocation();
  const {
    addToast
  } = useToasts();
  const {
    data: members
  } = useSWR(APIMethodKeys.organizations.listMembers(organization!.id), () => HookdeckAPI.organizations.listMembers());
  const is_free_plan = isFreePlan(subscription!.plan);
  const exceeds_users = subscription!.max_users && members && members.length >= subscription!.max_users;
  const invite_to_trial = !!exceeds_users && !organization?.sso_enabled && !subscription!.outpost_plan;
  const onClose = () => {
    history.push({
      pathname: '/settings/organization/members',
      search: location.search,
      state: {
        scroll: false
      }
    });
  };
  const validate = values => {
    if (!values[0]) {
      return {
        0: 'Required'
      };
    }
    if (!values['role0']) {
      return {
        role0: 'Required'
      };
    }
    return {};
  };
  const onSubmit = async values_0 => {
    const members_0: {
      email: string;
      role: OrganizationRole;
    }[] = [];
    for (let i = 0; i < Object.keys(values_0).filter(k => k.indexOf('role') >= 0).length; i++) {
      const email = values_0[i];
      const role = values_0[`role${i}`];
      if (email && role) {
        members_0.push({
          email,
          role
        });
      }
    }
    try {
      const {
        members: new_members,
        invites,
        subscription: subscription_0
      } = await HookdeckAPI.organizations.addMembers(members_0);
      onMembersAdded && onMembersAdded(new_members, invites, subscription_0);
      invites.forEach(invite => {
        Analytics.capture('Organization Member Invited', {
          organization_id: organization!.id,
          member_id: invite.user_id,
          member_email: invite.email,
          member_role: invite.role
        });
      });
      onClose();
    } catch (err) {
      addToast('error', err.response?.message || err.message);
    }
  };
  return <Formik initialValues={{
    role0: 'member'
  }} onSubmit={onSubmit} validate={validate}>
      {props => <form onSubmit={props.handleSubmit}>
          <Modal title="Invite to your organization" submit_label="Invite" cancel_label="Cancel" hide_close onSubmit={props.handleSubmit} onCancel={onClose} onClose={onClose} alt_action={is_free_plan ? {
        icon: 'upgrade',
        label: 'Upgrade plan',
        to: '/settings/organization/plans?highlight=max_users'
      } : undefined} is_submitting={props.isSubmitting}>
            {invite_to_trial && <Alert inline info m={{
          b: 4
        }}>
                Freely invite up to 25 users for a limited 14-day trial, beginning after the first
                user is added. Upgrade to keep users on this organization indefinitely.
              </Alert>}
            <Div>
              {new Array(Object.keys(props.values).filter(k_0 => k_0.indexOf('role') >= 0).length).fill(null).map((_, i_0) => <Div flex={{
            gap: 3
          }} key={i_0}>
                    <TextInput flex={{
              direction: 'column',
              grow: true
            }} name={`${i_0}`} placeholder="Email address" type="email" auto_complete="off" focus={i_0 === 0} />
                    <Div>
                      <SelectInput name={`role${i_0}`} dropdown={{
                w: {
                  px: 300
                },
                options_max_height: {
                  px: 330
                },
                parent_width: {
                  min: 120
                },
                placement: 'bottom-end'
              }} options={createRoleOptions(!!subscription?.features?.includes('viewer_role'), false)} />
                    </Div>
                    <Div>
                      <Button outline large icon="delete" onClick={() => {
                const new_values = {
                  ...props.values
                };
                delete new_values[`role${i_0}`];
                delete new_values[`${i_0}`];
                const count = Object.keys(props.values).filter(k_1 => k_1.indexOf('role') >= 0).length;
                for (let j = i_0 + 1; j < count; j++) {
                  new_values[j - 1] = new_values[j];
                  new_values[`role${j - 1}`] = new_values[`role${j}`];
                  delete new_values[j];
                  delete new_values[`role${j}`];
                }
                props.setValues(new_values);
              }} />
                    </Div>
                  </Div>)}
              <Div flex={{
            justify: 'flex-end'
          }}>
                <PlaceholderButton p={1.5} disabled={props.isSubmitting} onClick={() => {
              const count_0 = Object.keys(props.values).filter(k_2 => k_2.indexOf('role') >= 0).length;
              props.setFieldValue(`role${count_0}`, 'member');
            }}>
                  Add member
                </PlaceholderButton>
              </Div>
            </Div>
          </Modal>
        </form>}
    </Formik>;
};
export default AddOrganizationMemberModal;